import {inject, Injectable} from "@angular/core"
import {mutateThrowingErrors} from "@app/common/helpers/api/mutate"
import {MaterialAssetsRenderingService} from "@app/common/services/material-assets-rendering/material-assets-rendering.service"
import {MaterialMapsExporterService} from "@app/common/services/material-maps-exporter/material-maps-exporter.service"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {MaterialOutputFragment} from "@app/platform/components/materials/material-outputs/material-outputs.generated"
import {FlatThumbnailOptionLabels} from "@app/platform/models/state-labels"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import type {PbrExport} from "@cm/material-maps-exporter"
import {JobState, MaterialOutputType} from "@generated"
import {
    MaterialOutputsCancelJobGQL,
    MaterialOutputsCreateMaterialOutputGQL,
    MaterialOutputsDeleteDataObjectGQL,
    MaterialOutputsDeleteJsonFileGQL,
    MaterialOutputsDeleteOutputGQL,
    MaterialOutputsRestartJobGQL,
} from "@platform/services/material/material-output.generated"
import {environment} from "@environment"

type MaterialOutputPartial = Pick<MaterialOutputFragment, "id" | "job" | "result" | "config">

@Injectable()
export class MaterialOutputService {
    readonly materialAssetsRenderingService = inject(MaterialAssetsRenderingService)
    readonly materialMapsExporterService = inject(MaterialMapsExporterService)
    readonly notifications = inject(NotificationsService)
    readonly permission = inject(PermissionsService)
    readonly sceneManagerService = inject(SceneManagerService)

    readonly cancelMaterialOutputJobGql = inject(MaterialOutputsCancelJobGQL)
    readonly createMaterialOutputGql = inject(MaterialOutputsCreateMaterialOutputGQL)
    readonly deleteDataObjectGql = inject(MaterialOutputsDeleteDataObjectGQL)
    readonly deleteJsonFileGql = inject(MaterialOutputsDeleteJsonFileGQL)
    readonly deleteMaterialOutputGql = inject(MaterialOutputsDeleteOutputGQL)
    readonly restartMaterialOutputJobGql = inject(MaterialOutputsRestartJobGQL)

    async createOutput(settings: {
        pbrExportRequest?: PbrExport.Types.Request
        materialId: string
        materialRevisionId: string
        showNotification: boolean
        type: MaterialOutputType
        rethrowCaughtError?: boolean
    }) {
        const {pbrExportRequest, materialId, materialRevisionId, showNotification, type, rethrowCaughtError} = settings
        let jobId: string | undefined
        let configJsonObjectId: string | undefined
        switch (type) {
            case MaterialOutputType.TileableImage: {
                try {
                    jobId = await this.materialAssetsRenderingService.generateTile(
                        materialId,
                        environment.rendering.materialAssets.samples,
                        this.sceneManagerService,
                    )
                    if (showNotification) {
                        this.notifications.showInfo("Tileable image generation: Job submitted", undefined, "Dismiss")
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`Tileable image generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            case MaterialOutputType.ShaderBall: {
                try {
                    jobId = await this.materialAssetsRenderingService.generateShaderBallThumbnail(materialId, this.sceneManagerService)
                    if (showNotification) {
                        this.notifications.showInfo("Shader ball generation: Job submitted", undefined, "Dismiss")
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`Shader ball generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            case MaterialOutputType.Thumbnail10x10cm:
            case MaterialOutputType.Thumbnail15x15cm:
            case MaterialOutputType.Thumbnail20x20cm:
            case MaterialOutputType.Thumbnail30x30cm:
            case MaterialOutputType.Thumbnail7x7inches:
            case MaterialOutputType.Thumbnail9x9inches:
            case MaterialOutputType.Thumbnail16x16inches:
            case MaterialOutputType.Thumbnail8x8inches:
            case MaterialOutputType.Thumbnail13_5x13_5inches:
            case MaterialOutputType.Thumbnail32x32inches:
            case MaterialOutputType.Thumbnail27x27inches: {
                try {
                    const flatOption = FlatThumbnailOptionLabels.get(type)?.state
                    if (flatOption && this.materialAssetsRenderingService.checkThumbnailSizeValid(flatOption.resolution, flatOption.size)) {
                        jobId = await this.materialAssetsRenderingService.generateThumbnail(
                            materialId,
                            environment.rendering.materialAssets.samples,
                            flatOption,
                            this.sceneManagerService,
                        )
                        if (showNotification) {
                            this.notifications.showInfo("Flat thumbnail generation: Job submitted", undefined, "Dismiss")
                        }
                    } else {
                        throw new Error("Requested resolution too high")
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`Flat thumbnail generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            case MaterialOutputType.PbrExportDefaultJpegLow:
            case MaterialOutputType.PbrExportDefaultJpegOriginal:
            case MaterialOutputType.PbrExportDefaultTiffLow:
            case MaterialOutputType.PbrExportDefaultTiffOriginal:
            case MaterialOutputType.PbrExportDefaultExrOriginal:
            case MaterialOutputType.PbrExportCustom: {
                try {
                    if (!pbrExportRequest) {
                        throw new Error("PBR export request missing")
                    }
                    const mapsExportIds = await this.materialMapsExporterService.generatePbrExport(pbrExportRequest, {id: materialId})
                    jobId = mapsExportIds.jobId
                    configJsonObjectId = mapsExportIds.configJsonObjectId
                    if (showNotification) {
                        this.notifications.showInfo(`PBR export generation: Job submitted (${jobId})`)
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`PBR export generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            case MaterialOutputType.PconExport: {
                try {
                    jobId = (await this.materialMapsExporterService.generatePconExport({id: materialId})).jobId
                    if (showNotification) {
                        this.notifications.showInfo(`pCon export generation: Job submitted (${jobId})`)
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`pCon export generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                    return
                }
                break
            }
            case MaterialOutputType.FabricOverview: {
                try {
                    jobId = await this.materialAssetsRenderingService.generateFabricOverview(materialId)
                    if (showNotification) {
                        this.notifications.showInfo("Fabric overview generation: Job submitted", undefined, "Dismiss")
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`Fabric overview generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            default:
                break
        }

        if (jobId) {
            await mutateThrowingErrors(this.createMaterialOutputGql)({
                input: {
                    configId: configJsonObjectId,
                    jobId,
                    materialId,
                    materialRevisionId,
                    type,
                },
            })
        } else {
            if (showNotification) {
                this.notifications.showInfo("Error creating job", 3000)
            }
        }
    }

    async cancelAndRemoveOutput({materialOutput, showNotification}: {materialOutput: MaterialOutputPartial; showNotification: boolean}) {
        if (materialOutput.job?.state === JobState.Init || materialOutput.job?.state === JobState.Runnable || materialOutput.job?.state === JobState.Running) {
            await mutateThrowingErrors(this.cancelMaterialOutputJobGql)({id: materialOutput.job.id})
        }

        if (materialOutput.config) {
            await mutateThrowingErrors(this.deleteJsonFileGql)({id: materialOutput.config.id}).catch(() => {
                if (showNotification) {
                    this.notifications.showInfo("Cannot delete config.", 3000)
                }
            })
        }

        if (materialOutput.result) {
            await mutateThrowingErrors(this.deleteDataObjectGql)({id: materialOutput.result.id}).catch(() => {
                if (showNotification) {
                    this.notifications.showInfo("Cannot delete file.", 3000)
                }
            })
        } else {
            // referential integrity should take care of this at the DB level when the result is deleted
            // so we only need to delete explicitly when there is no result
            await mutateThrowingErrors(this.deleteMaterialOutputGql)({id: materialOutput.id})
        }
    }

    async restartOutput({materialOutput, showNotification}: {materialOutput: MaterialOutputPartial; showNotification: boolean}) {
        if (materialOutput.job?.id) {
            await mutateThrowingErrors(this.restartMaterialOutputJobGql)({id: materialOutput.job.id})
        } else {
            if (showNotification) {
                this.notifications.showInfo("Cannot restart job without job id", 3000)
            }
        }
    }
}
